const _throttle = require('lodash.throttle');

window.addEventListener("load", () => {
    const addClickListenerToCardPanel = (cardPanel) => {
        const hasVariantCards = cardPanel.querySelector("[data-hd-card],[data-vt-card]");
        if (!hasVariantCards) return;

        cardPanel.addEventListener("click", (event) => {
            const buttonClickedToggleLightMode = event.target.closest("button[data-toggle-light-mode]");
            const buttonClickedToggleImageView = event.target.closest("button[data-toggle-image-view]");
            const buttonClickedToggleStackedView = event.target.closest("button[data-toggle-stack-view]");

            if (buttonClickedToggleLightMode) {
                const isActive = buttonClickedToggleLightMode.dataset.active === 'true';
                buttonClickedToggleLightMode.dataset.active = isActive ? 'false' : 'true';
                cardPanel.dataset.lightCards = isActive ? 'false' : 'true';
            }

            if (buttonClickedToggleImageView) {
                const cardImageContainers = cardPanel.querySelectorAll("[data-hd-card] [data-default-floorplan]");
                const isActive = buttonClickedToggleImageView.dataset.active === 'true';
                buttonClickedToggleImageView.dataset.active = isActive ? 'false' : 'true';

                for (const cardImageContainer of cardImageContainers) {
                    cardImageContainer.dataset.defaultFloorplan = isActive ? 'false' : 'true';
                    cardImageContainer.dataset.showingFloorplan = isActive ? 'false' : 'true';
                }
            }

            if (buttonClickedToggleStackedView) {
                const isActive = buttonClickedToggleStackedView.dataset.active === 'true';
                buttonClickedToggleStackedView.dataset.active = isActive ? 'false' : 'true';

                const cardContainer = cardPanel.querySelector(".grid[data-grid-view]");

                if (cardContainer && buttonClickedToggleStackedView.dataset.active === 'true') {
                    cardContainer.dataset.gridView = 'false';
                } else if (cardContainer && buttonClickedToggleStackedView.dataset.active === 'false') {
                    cardContainer.dataset.gridView = 'true';
                }
            }
        });
    };

    const initializeFilters = (cardPanel) => {
        initializeClearFilters(cardPanel);
        initializeClickFilters(cardPanel);
        initializeSelectFilters(cardPanel);
        initializeTextFilters(cardPanel);
    };

    const initializeClearFilters = (cardPanel) => {
        const clearFilters = cardPanel.querySelector("[data-clear-filters]");
        clearFilters?.addEventListener("click", (e) => {
            const selectFilters = cardPanel.querySelectorAll("[data-filter-type='select']");
            selectFilters.forEach((filter) => {
                const firstOption = filter.querySelector("option");
                filter.value = firstOption.value;
            });

            const textFilters = cardPanel.querySelectorAll("[data-filter-type='text']");
            textFilters.forEach((filter) => {
                filter.value = "";
                const container = filter.closest('[data-search-container]');
                if (container) {
                    container.dataset.open = 'false';
                }
            });

            applyFilters(cardPanel);
        });
    };

    const initializeClickFilters = (cardPanel) => {
        const clickFilters = cardPanel.querySelectorAll("[data-filter-type='click']");
        clickFilters.forEach((filter) => {
            filter.addEventListener('click', (e) => {
                const filterField = filter.getAttribute('data-filter-field');
                const otherFilters = cardPanel.querySelectorAll(`[data-filter-type='click'][data-filter-field="${filterField}"]`);
                otherFilters.forEach((otherFilter) => {
                    if (otherFilter !== filter) {
                        otherFilter.setAttribute('data-active', false);
                    }
                });
                filter.setAttribute('data-active', true);
                applyFilters(cardPanel);
            });
        });
    };

    const initializeSelectFilters = (cardPanel) => {
        const selectFilters = cardPanel.querySelectorAll("[data-filter-type='select']");
        selectFilters.forEach((filter) => {
            filter.addEventListener('change', (e) => {
                applyFilters(cardPanel);
            });
        });
    };

    const initializeTextFilters = (cardPanel) => {
        const textFilters = cardPanel.querySelectorAll("[data-filter-type='text']");
        textFilters.forEach((filter) => {
            const debounceHandler = debounce(() => {applyFilters(cardPanel)}, 1000);
            filter.addEventListener('input', debounceHandler);
        });
    };

    const debounce = (callback, waitTime) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                callback(...args);
            }, waitTime);
        };
    }

    const applyFilters = (cardPanel) => {
        const filters = getActiveFilters(cardPanel);
        const filterables = cardPanel.querySelectorAll("[data-hd-filter-data]");
        filterables.forEach((filterable) => {
            let showThis = true;
            for (let [filterField, filterValue] of Object.entries(filters)) {
                if (showThis) {
                    // const attributeName = (filterField in filterFieldDataFieldMap) ? `data-${filterFieldDataFieldMap[filterField]}` : `data-${filterField}`;
                    const attributeName = `data-hd-variant-${filterField}`;
                    let filterableValue = filterable.getAttribute(attributeName);
                    let isFilterSatisfied = false;
                    if (filterValue === "Any" || filterValue === "All") {
                        isFilterSatisfied = true;
                    } else {
                        // For fields that need special logic.
                        switch (filterField) {
                            case 'price-number':
                                let cardPrice = parseFloat(filterableValue);
                                switch ( filterValue ) {
                                    case "< 500K":
                                        isFilterSatisfied = cardPrice < 500000;
                                        break;
                                    case "500K - 600K":
                                        isFilterSatisfied = cardPrice >= 500000 && cardPrice <= 600000;
                                        break;
                                    case "600K - 700K":
                                        isFilterSatisfied = cardPrice >= 600000 && cardPrice <= 700000;
                                        break;
                                    case "700K - 800K":
                                        isFilterSatisfied = cardPrice >= 700000 && cardPrice <= 800000;
                                        break;
                                    case "800K - 900K":
                                        isFilterSatisfied = cardPrice >= 800000 && cardPrice <= 900000;
                                        break;
                                    case "900K - 1M":
                                        isFilterSatisfied = cardPrice >= 900000 && cardPrice <= 1000000;
                                        break;
                                    case "> 1M":
                                        isFilterSatisfied = cardPrice >= 1000000;
                                        break;
                                    default:
                                        isFilterSatisfied = true;
                                }
                                break;

                            case 'house-width':
                            case 'min-block-width':
                                if (filterableValue) {
                                    let houseWidth = parseFloat(filterableValue.replace("m", ""));
                                    switch ( filterValue ) {
                                        case "Less than 12.5m":
                                            isFilterSatisfied = houseWidth < 12.5;
                                            break;
                                        case "12.5m to 15m":
                                            isFilterSatisfied = houseWidth >= 12.5 && houseWidth <= 15;
                                            break;
                                        case "More than 15m":
                                            isFilterSatisfied = houseWidth >= 15;
                                            break;
                                        default:
                                            isFilterSatisfied = true;
                                    }
                                } else {
                                    isFilterSatisfied = true;
                                }
                                break;

                            case 'range':
                                isFilterSatisfied = (filterValue === 'All') || (filterableValue === filterValue);
                                break;

                            case 'title':
                                if (filterValue.length) {
                                    let searchHit = true;
                                    const filterWords = filterValue.toLowerCase().split(" ");
                                    for (let filterWord of filterWords) {
                                        searchHit = searchHit && filterableValue.toLowerCase().includes(filterWord);
                                    }
                                    isFilterSatisfied = searchHit;
                                } else {
                                    isFilterSatisfied = true;
                                }
                                break;

                            default:
                                isFilterSatisfied = filterableValue === filterValue;
                        }
                    }
                    showThis = showThis && isFilterSatisfied;
                }
                if (!showThis) {
                    break;
                }
            }
            if (showThis) {
                showElement(filterable);
            } else {
                hideElement(filterable);
            }
            showOrHideFamilyCard(filterable);
        });

        const resultCount = cardPanel.querySelectorAll('[data-hd-card]:not(.hidden),[data-vt-card]:not(.hidden)').length;
        const emptyResultsMessage = cardPanel.querySelector('[data-filter-results-empty]');
        if (resultCount > 0) {
            hideElement(emptyResultsMessage);
            showOrHideVirtualTourIframe(cardPanel, true);
        } else {
            showElement(emptyResultsMessage);
            showOrHideVirtualTourIframe(cardPanel, false);
        }

        updateCardOrder();
        clickFirstVirtualTourCard(cardPanel);
    };
    
    const clickFirstVirtualTourCard = (cardPanel) => {
        const firstVirtualTourCard = cardPanel.querySelector('[data-vt-card]:not(.hidden)');
        if (firstVirtualTourCard) {
            firstVirtualTourCard.click();
        }
    };
    
    const showOrHideVirtualTourIframe = (cardPanel, show) => {
        const iFrame = cardPanel.querySelector('iframe');
        if (iFrame) {
            if (show) {
                showElement(iFrame);
            } else {
                hideElement(iFrame);
            }
        }
    }

    const _throttle = require('lodash.throttle');

    function updateCardOrder() {
        const cardPanels = document.querySelectorAll('.house-card-grid-panel.house-card-grid-panel-house-land-packages, .house-card-grid-panel.house-card-grid-panel-home-design-families, .house-card-grid-panel.house-card-grid-panel-home-design-variants');

        for (const cardPanel of cardPanels) {
            const allCards = cardPanel.querySelectorAll('div[data-hd-card]');
            const activeCards = Array.from(allCards).filter(card => !card.classList.contains('hidden'));

            allCards.forEach(card => card.style.order = 'unset');

            activeCards.forEach((card, index) => {
                if (index === 0) card.style.order = -4;
                else if (index === 1) card.style.order = window.innerWidth >= 768 ? -3 : 'unset';
                else if (index === 2) card.style.order = window.innerWidth >= 1024 ? -2 : 'unset';
                else card.style.order = 'unset';
            });
        }
    }

    // Run immediately in case `DOMContentLoaded` already fired
    updateCardOrder();

    // Run on window resize (throttled)
    window.addEventListener("resize", _throttle(updateCardOrder, 200));

    const getActiveFilters = (cardPanel) => {
        let filters = {};
        const clickFilters = cardPanel.querySelectorAll("[data-filter-type='click'][data-active='true']");
        const textAndSelectFilters = cardPanel.querySelectorAll("[data-filter-type='select'], [data-filter-type='text']");
        clickFilters.forEach((filter) => {
            const filterField = filter.getAttribute('data-filter-field');
            const filterValue = filter.getAttribute('data-filter-value');
            if (filterValue && filterValue.length) {
                filters[filterField] = filterValue;
            }
        });
        textAndSelectFilters.forEach((filter) => {
            const filterField = filter.getAttribute('data-filter-field');
            const filterValue = filter.value;
            if (filterValue && filterValue.length) {
                filters[filterField] = filterValue;
            }
        });
        return filters;
    };

    const showElement = (element) => {
        const variantSwitcher = element.closest("[data-hd-switcher]");
        if (variantSwitcher) {
            element.disabled = false;
        } else {
            const target = element.closest("[data-hd-card]");
            if (target) {
                target.classList.remove('hidden');
            } else {
                element.classList.remove('hidden');
            }
        }
    };

    const hideElement = (element) => {
        const variantSwitcher = element.closest("[data-hd-switcher]");
        if (variantSwitcher) {
            element.disabled = true;
            element.setAttribute("data-active", false);
        } else {
            const target = element.closest("[data-hd-card]");
            if (target) {
                target.classList.add('hidden');
            }
            else {
                element.classList.add('hidden');
            }
        }
    };

    const showOrHideFamilyCard = (element) => {
        const variantSwitcher = element.closest("[data-hd-switcher]");
        if (variantSwitcher) {
            const familyCard = element.closest("[data-hd-card]");
            let buttonHasBeenActivated = false;
            const buttons = familyCard?.querySelectorAll("button[data-hd-filter-data]");
            buttons.forEach((button) => {
                if (!button.disabled) {
                    if (!buttonHasBeenActivated) {
                        button.click();
                        buttonHasBeenActivated = true;
                    }
                }
            });

            const activeButtons = familyCard?.querySelectorAll("button[data-hd-filter-data]:not([disabled])").length;
            if (activeButtons > 0) {
                showElement(familyCard);
            } else {
                hideElement(familyCard);
            }
        }
    };

    // Function to get URL query parameter, including after the hash if present
    function getQueryParam(param) {
        const url = window.location.href; // Get the full URL
        const urlParts = url.split('#');  // Split URL into two parts: before and after the '#'
        const queryString = urlParts[0].split('?')[1] || '';  // Get the query string before the '#'
        const hashString = urlParts[1] ? urlParts[1].split('?')[1] : '';  // Get the query string after the '#'

        // Combine both query strings and parse them
        const fullQueryString = queryString + (hashString ? '&' + hashString : '');
        const urlParams = new URLSearchParams(fullQueryString);
        return urlParams.get(param);
    }

    // Function to check and set button state based on URL parameter
    function handleFilterRange(cardPanel, paramName) {
        const filterRangeParam = getQueryParam(paramName);
        if (filterRangeParam) {
            const capitalizedFilterRangeParam = filterRangeParam.charAt(0).toUpperCase() + filterRangeParam.slice(1).toLowerCase();
            const button = document.querySelector(`button[data-filter-field="range"][data-filter-value="${capitalizedFilterRangeParam}"]`);

            if (button && button.getAttribute('data-active') === 'false') {
                const filterField = button.getAttribute('data-filter-field');
                const otherFilters = cardPanel.querySelectorAll(`[data-filter-type='click'][data-filter-field="${filterField}"]`);
                otherFilters.forEach((otherFilter) => {
                    if (otherFilter !== button) {
                        otherFilter.setAttribute('data-active', false);
                    }
                });
                button.setAttribute('data-active', true);
                applyFilters(cardPanel);
            }
        }
    }

    // Function to decode URL parameter value and match with <select> options
    function setSelectOptions(cardPanel, selectParams) {
        selectParams.forEach(({ selectId, paramName }) => {
            const paramValue = getQueryParam(paramName);
            if (paramValue) {
                const decodedValue = decodeURIComponent(paramValue); // Decode URL-encoded value
                const select = cardPanel.querySelector(`select[id="${selectId}"]`);

                if (select) {
                    // Capitalize the first letter of the entire string
                    const capitalizedFilterRangeParam = decodedValue.charAt(0).toUpperCase() + decodedValue.slice(1).toLowerCase();

                    // Capitalize the first letter of each word
                    const capitalizedAllFilterRangeParam = decodedValue
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');

                    // Replace all lowercase 'k' and 'm' with uppercase
                    const lowerToUpperCaseParam = decodedValue.replace(/[km]/g, (match) => match.toUpperCase());

                    console.log('decodedValue: ', decodedValue);
                    console.log('capitalizedFilterRangeParam: ', capitalizedFilterRangeParam);
                    console.log('capitalizedAllFilterRangeParam: ', capitalizedAllFilterRangeParam);
                    console.log('lowerToUpperCaseParam: ', lowerToUpperCaseParam);

                    // Find the option that matches any of the decoded value's formats
                    const option = Array.from(select.options).find(opt => {
                        const trimmedText = opt.text.trim();
                        return (
                            trimmedText === decodedValue ||
                            trimmedText === capitalizedFilterRangeParam ||
                            trimmedText === capitalizedAllFilterRangeParam ||
                            trimmedText === lowerToUpperCaseParam
                        );
                    });

                    if (option) {
                        select.value = option.value; // Set the select to the matched option
                        applyFilters(cardPanel);
                    }
                }

            }
        });
    }

    /**
     * Start of main code
     */
    const validCardPanels = document.querySelectorAll(".house-card-grid-panel.house-card-grid-panel-filtered,.virtual-tour-panel");

    for (const cardPanel of validCardPanels) {
        addClickListenerToCardPanel(cardPanel);
        initializeFilters(cardPanel);

        // Check filter range and update button state
        handleFilterRange(cardPanel, 'filter-range');

        // Set the select options based on URL parameters
        setSelectOptions(cardPanel, [
            { selectId: 'Block Width', paramName: 'filter-block-width' },
            { selectId: 'Price', paramName: 'filter-price' },
            { selectId: 'Storeys', paramName: 'filter-storeys' },
            { selectId: 'Beds', paramName: 'filter-beds' },
            { selectId: 'Baths', paramName: 'filter-baths' },
            { selectId: 'Variation Type', paramName: 'filter-variation-type' }
        ]);
    }
});
